import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useBrand } from "@/contexts/BrandContext";
import { Save, Check } from "lucide-react";
import type { Restaurant } from "@/hooks/useRestaurant";

const TEMPLATES = [
  { id: "classic", name: "Classic", description: "Bold hero with order-type cards, featured items grid, and promo carousel.", preview: "🍔" },
  { id: "elegant", name: "Elegant", description: "Dark cinematic hero, centered typography, signature collection layout.", preview: "🍷" },
  { id: "modern", name: "Modern", description: "Gradient hero with quick-action buttons, trending section, and stats band.", preview: "⚡" },
  { id: "landing", name: "Landing Page", description: "Single-page layout: full-screen hero, menu highlights, story, and branches.", preview: "🚀" },
  { id: "heritage", name: "Heritage", description: "Rich editorial layout with story, category explorer, picks, and visit section.", preview: "🏛️" },
] as const;

type Props = {
  restaurant: Restaurant | null;
  onUpdate: (updates: Partial<Restaurant>) => Promise<{ data: any; error: any } | undefined>;
};

export default function AdminBranding({ restaurant, onUpdate }: Props) {
  const { toast } = useToast();
  const { refetchBrand } = useBrand();
  const [saving, setSaving] = useState(false);

  // Identity
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [logoType, setLogoType] = useState("emoji");
  const [logoEmoji, setLogoEmoji] = useState("🍔");
  const [logoUrl, setLogoUrl] = useState("");
  const [tagline, setTagline] = useState("");
  const [taglineAr, setTaglineAr] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [primaryColor, setPrimaryColor] = useState("#FF6B35");
  const [secondaryColor, setSecondaryColor] = useState("#2A9D8F");
  const [template, setTemplate] = useState("classic");

  // Business settings
  const [productsAvailableInAllBranches, setProductsAvailableInAllBranches] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [autoAcceptOrder, setAutoAcceptOrder] = useState(false);
  const [paymentApprovalMode, setPaymentApprovalMode] = useState("auto");
  const [allowCoupon, setAllowCoupon] = useState(true);
  const [orderMinimumAllowed, setOrderMinimumAllowed] = useState(0);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [isDeliveryActive, setIsDeliveryActive] = useState(true);
  const [isPickUpActive, setIsPickUpActive] = useState(true);
  const [isDineInActive, setIsDineInActive] = useState(false);
  const [vatRate, setVatRate] = useState(0);
  const [taxInclusive, setTaxInclusive] = useState(false);
  useEffect(() => {
    if (restaurant) {
      setName(restaurant.name || "");
      setNameAr(restaurant.name_ar || "");
      setLogoType(restaurant.logo_type || "emoji");
      setLogoEmoji(restaurant.logo_emoji || "🍔");
      setLogoUrl(restaurant.logo_url || "");
      setTagline(restaurant.tagline || "");
      setTaglineAr(restaurant.tagline_ar || "");
      setCurrencySymbol(restaurant.currency_symbol || "$");
      setCurrencyCode(restaurant.currency_code || "USD");
      setPrimaryColor(restaurant.primary_color || "#FF6B35");
      setSecondaryColor(restaurant.secondary_color || "#2A9D8F");
      setTemplate(restaurant.template || "classic");
      setProductsAvailableInAllBranches(restaurant.products_available_in_all_branches ?? false);
      setPaymentMethod(restaurant.payment_method || "card");
      setAutoAcceptOrder(restaurant.auto_accept_order ?? false);
      setPaymentApprovalMode(restaurant.payment_approval_mode || "auto");
      setAllowCoupon(restaurant.allow_coupon ?? true);
      setOrderMinimumAllowed(restaurant.order_minimum_allowed ?? 0);
      setHasSchedule(restaurant.has_schedule ?? false);
      setIsDeliveryActive(restaurant.is_delivery_active ?? true);
      setIsPickUpActive(restaurant.is_pickup_active ?? true);
      setIsDineInActive((restaurant as any).is_dinein_active ?? false);
      setVatRate(restaurant.vat_rate ?? 0);
      setTaxInclusive((restaurant as any).tax_inclusive ?? false);
    }
  }, [restaurant]);

  const handleSave = async () => {
    setSaving(true);
    const result = await onUpdate({
      name, name_ar: nameAr, logo_type: logoType, logo_emoji: logoEmoji, logo_url: logoUrl,
      tagline, tagline_ar: taglineAr, currency_symbol: currencySymbol, currency_code: currencyCode,
      primary_color: primaryColor, secondary_color: secondaryColor, template,
      products_available_in_all_branches: productsAvailableInAllBranches,
      payment_method: paymentMethod,
      auto_accept_order: autoAcceptOrder,
      payment_approval_mode: paymentApprovalMode,
      allow_coupon: allowCoupon,
      order_minimum_allowed: orderMinimumAllowed,
      has_schedule: hasSchedule,
      is_delivery_active: isDeliveryActive,
      is_pickup_active: isPickUpActive,
      is_dinein_active: isDineInActive,
      vat_rate: vatRate,
      tax_inclusive: taxInclusive,
    });
    setSaving(false);
    if (result?.error) {
      toast({ title: "Error saving", description: result.error.message, variant: "destructive" });
    } else {
      await refetchBrand();
      toast({ title: "Branding updated!" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Template Selector */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Homepage Template</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TEMPLATES.map((tmpl) => (
              <button
                key={tmpl.id}
                onClick={() => setTemplate(tmpl.id)}
                className={`relative flex flex-col items-center gap-3 rounded-xl border-2 p-6 text-center transition-all duration-200 ${
                  template === tmpl.id
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-border bg-card hover:border-muted-foreground/30"
                }`}
              >
                {template === tmpl.id && (
                  <div className="absolute top-2 end-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <span className="text-4xl">{tmpl.preview}</span>
                <h3 className="font-heading font-bold text-foreground">{tmpl.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{tmpl.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Restaurant Identity</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Restaurant Name (EN)</Label><Input value={name} onChange={e => setName(e.target.value)} /></div>
            <div><Label>Restaurant Name (AR)</Label><Input value={nameAr} onChange={e => setNameAr(e.target.value)} dir="rtl" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Tagline (EN)</Label><Input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="Fresh food, bold flavors" /></div>
            <div><Label>Tagline (AR)</Label><Input value={taglineAr} onChange={e => setTaglineAr(e.target.value)} dir="rtl" placeholder="طعام طازج، نكهات جريئة" /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Logo</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Logo Type</Label>
            <Select value={logoType} onValueChange={setLogoType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="emoji">Emoji</SelectItem>
                <SelectItem value="image">Image URL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {logoType === "emoji" ? (
            <div className="flex items-center gap-4">
              <div><Label>Emoji</Label><Input value={logoEmoji} onChange={e => setLogoEmoji(e.target.value)} className="w-20 text-2xl text-center" /></div>
              <div className="w-16 h-16 rounded-xl bg-primary flex items-center justify-center text-3xl">{logoEmoji}</div>
            </div>
          ) : (
            <div className="space-y-2">
              <div><Label>Image URL</Label><Input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://..." /></div>
              {logoUrl && <img src={logoUrl} alt="Logo preview" className="w-16 h-16 rounded-xl object-cover" />}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Currency & Colors</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Currency Symbol</Label><Input value={currencySymbol} onChange={e => setCurrencySymbol(e.target.value)} className="w-20" /></div>
            <div><Label>Currency Code</Label><Input value={currencyCode} onChange={e => setCurrencyCode(e.target.value)} className="w-24" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-28" />
              </div>
            </div>
            <div>
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} className="w-28" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Order Settings */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Order Settings</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Products Available in All Branches</Label>
              <p className="text-xs text-muted-foreground">When enabled, cart is preserved when switching between pickup/delivery. When disabled, cart clears on order type change.</p>
            </div>
            <Switch checked={productsAvailableInAllBranches} onCheckedChange={setProductsAvailableInAllBranches} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Delivery Active</Label>
              <p className="text-xs text-muted-foreground">Allow customers to place delivery orders</p>
            </div>
            <Switch checked={isDeliveryActive} onCheckedChange={setIsDeliveryActive} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Pickup Active</Label>
              <p className="text-xs text-muted-foreground">Allow customers to place pickup orders</p>
            </div>
            <Switch checked={isPickUpActive} onCheckedChange={setIsPickUpActive} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Dine-In Active</Label>
              <p className="text-xs text-muted-foreground">Allow customers to place dine-in orders</p>
            </div>
            <Switch checked={isDineInActive} onCheckedChange={setIsDineInActive} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Auto-Accept Orders</Label>
              <p className="text-xs text-muted-foreground">Automatically accept incoming orders without manual review</p>
            </div>
            <Switch checked={autoAcceptOrder} onCheckedChange={setAutoAcceptOrder} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Allow Coupons</Label>
              <p className="text-xs text-muted-foreground">Show promo code input on the cart page</p>
            </div>
            <Switch checked={allowCoupon} onCheckedChange={setAllowCoupon} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Scheduled Orders</Label>
              <p className="text-xs text-muted-foreground">Allow customers to schedule orders for later</p>
            </div>
            <Switch checked={hasSchedule} onCheckedChange={setHasSchedule} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Approval Mode</Label>
              <Select value={paymentApprovalMode} onValueChange={setPaymentApprovalMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatic</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Minimum Order Amount</Label>
              <Input type="number" min={0} step={0.01} value={orderMinimumAllowed} onChange={e => setOrderMinimumAllowed(parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <Label>VAT Rate (%)</Label>
              <Input type="number" min={0} max={100} step={0.1} value={vatRate} onChange={e => setVatRate(parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Tax Inclusive</Label>
              <p className="text-xs text-muted-foreground">When enabled, prices already include tax (tax will show as 0). When disabled, tax is calculated on top of subtotal.</p>
            </div>
            <Switch checked={taxInclusive} onCheckedChange={setTaxInclusive} />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Branding"}
      </Button>
    </div>
  );
}
